import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FirmBaseLayoutContext } from './FirmBaseLayoutContext';
import type {
  FirmBaseLayoutContextValue,
  FirmBaseLayoutNavigationRegistrationToken,
  FirmBaseLayoutSecondaryNavigationContributionRegistration,
  FirmBaseLayoutSecondaryNavigationRegistration,
  FirmBaseLayoutTertiaryNavigationRegistration,
} from './FirmBaseLayoutContext.types';
import type { SecondaryNavigationState } from './secondaryNavigation.types';
import { areSecondaryNavigationStatesEqual } from './secondaryNavigation.utilities';
import type { SecondaryNavigationContributionState } from './secondaryNavigationContribution.types';
import type { TertiaryNavigationState } from './tertiaryNavigation.types';
import { areTertiaryNavigationStatesEqual } from './tertiaryNavigation.utilities';

interface FirmBaseLayoutContextProviderProps {
    children: ReactNode;
}

const FirmBaseLayoutContextProvider = ({children}: FirmBaseLayoutContextProviderProps) => {
  const [secondaryNavigationRegistration, setSecondaryNavigationRegistration] =
        useState<FirmBaseLayoutSecondaryNavigationRegistration | null>(null);
  const [secondaryNavigationContributionRegistration, setSecondaryNavigationContributionRegistration] =
        useState<FirmBaseLayoutSecondaryNavigationContributionRegistration | null>(null);
  const [tertiaryNavigationRegistration, setTertiaryNavigationRegistration] =
        useState<FirmBaseLayoutTertiaryNavigationRegistration | null>(null);

  // Route children register shell state by opaque owner token so unmount cleanup cannot clear a newer route.
  const registerSecondaryNavigation = useCallback(
    (
      registrationToken: FirmBaseLayoutNavigationRegistrationToken,
      nextSecondaryNavigation: SecondaryNavigationState | null
    ) => {
      setSecondaryNavigationRegistration(previousRegistration => {
        if (
          previousRegistration?.registrationToken === registrationToken
                    && areSecondaryNavigationStatesEqual(previousRegistration.navigation, nextSecondaryNavigation)
        ) {
          return previousRegistration;
        }

        return {
          registrationToken,
          navigation: nextSecondaryNavigation,
        };
      });
    },
    []
  );

  const clearSecondaryNavigation = useCallback((registrationToken: FirmBaseLayoutNavigationRegistrationToken) => {
    setSecondaryNavigationRegistration(previousRegistration =>
      previousRegistration?.registrationToken === registrationToken ? null : previousRegistration
    );
  }, []);

  const registerSecondaryNavigationContribution = useCallback(
    (
      registrationToken: FirmBaseLayoutNavigationRegistrationToken,
      nextContribution: SecondaryNavigationContributionState | null
    ) => {
      setSecondaryNavigationContributionRegistration(previousRegistration => {
        if (previousRegistration?.registrationToken === registrationToken
                    && previousRegistration.navigation === nextContribution) {
          return previousRegistration;
        }

        return {registrationToken, navigation: nextContribution};
      });
    },
    []
  );

  const clearSecondaryNavigationContribution = useCallback(
    (registrationToken: FirmBaseLayoutNavigationRegistrationToken) => {
      setSecondaryNavigationContributionRegistration(previousRegistration =>
        previousRegistration?.registrationToken === registrationToken ? null : previousRegistration
      );
    },
    []
  );

  const registerTertiaryNavigation = useCallback(
    (
      registrationToken: FirmBaseLayoutNavigationRegistrationToken,
      nextTertiaryNavigation: TertiaryNavigationState | null
    ) => {
      setTertiaryNavigationRegistration(previousRegistration => {
        if (
          previousRegistration?.registrationToken === registrationToken
                    && areTertiaryNavigationStatesEqual(previousRegistration.navigation, nextTertiaryNavigation)
        ) {
          return previousRegistration;
        }

        return {
          registrationToken,
          navigation: nextTertiaryNavigation,
        };
      });
    },
    []
  );

  const clearTertiaryNavigation = useCallback((registrationToken: FirmBaseLayoutNavigationRegistrationToken) => {
    setTertiaryNavigationRegistration(previousRegistration =>
      previousRegistration?.registrationToken === registrationToken ? null : previousRegistration
    );
  }, []);

  const secondaryNavigation = useMemo<SecondaryNavigationState | null>(() => {
    const base = secondaryNavigationRegistration?.navigation ?? null;
    const contribution = secondaryNavigationContributionRegistration?.navigation ?? null;

    if (!base) {
      return null;
    }
    if (!contribution) {
      return base;
    }

    return {
      ...base,
      actions: contribution.actions ?? base.actions,
      actionsNode: contribution.actionsNode ?? base.actionsNode,
      status: contribution.status ?? base.status,
      titleAccessory: contribution.titleAccessory ?? base.titleAccessory,
      titleAccessoryNode: contribution.titleAccessoryNode ?? base.titleAccessoryNode,
      titleLeading: contribution.titleLeading ?? base.titleLeading,
    };
  }, [secondaryNavigationRegistration, secondaryNavigationContributionRegistration]);

  const value = useMemo<FirmBaseLayoutContextValue>(
    () => ({
      secondaryNavigation,
      tertiaryNavigation: tertiaryNavigationRegistration?.navigation ?? null,
      clearSecondaryNavigation,
      clearSecondaryNavigationContribution,
      clearTertiaryNavigation,
      registerSecondaryNavigation,
      registerSecondaryNavigationContribution,
      registerTertiaryNavigation,
    }),
    [
      secondaryNavigation,
      clearSecondaryNavigation,
      clearSecondaryNavigationContribution,
      clearTertiaryNavigation,
      registerSecondaryNavigation,
      registerSecondaryNavigationContribution,
      registerTertiaryNavigation,
      tertiaryNavigationRegistration,
    ]
  );

  return <FirmBaseLayoutContext.Provider value={value}>{children}</FirmBaseLayoutContext.Provider>;
};

export default FirmBaseLayoutContextProvider;
